import { useState, useEffect } from 'react';
import { isGameFinished } from '../utils/constants';

const sortGamesByStatus = (games) => {
  return [...games].sort((a, b) => {
    // Move games with status 'Final' or 'Game Over' to the bottom
    if (isGameFinished(a.status) && !isGameFinished(b.status)) {
      return 1;
    }
    if (!isGameFinished(a.status) && isGameFinished(b.status)) {
      return -1;
    }
    // Keep original order for non-finished games
    return 0;
  });
};

export const useNcaafGames = (selectedSport, selectedWeek) => {
  const [games, setGames] = useState([]);
  const [gamesMetadata, setGamesMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchGamesForWeek = async (week = 'today', silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const endpoint = `/api/games/ncaaf/schedule/week/${week}`;
      console.log('useNcaafGames: Fetching from endpoint:', endpoint);
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('useNcaafGames: API response:', data);
      
      if (data.success) {
        // Swap ESPN's light-background team logo for the dark-background
        // variant (same path with /500/ → /500-dark/). App's dark theme
        // makes the standard logos look washed out.
        const darkLogo = (url) => (typeof url === 'string' ? url.replace('/500/', '/500-dark/') : url);
        // Transform the data for football-specific structure
        const transformedGames = data.games?.map(game => ({
          gameId: game.gameId, // Use proper field name for football
          gameIDmlb: game.gameId, // Keep for compatibility with shared code
          away: game.away,
          home: game.home,
          awayLogo: darkLogo(game.awayLogo),
          homeLogo: darkLogo(game.homeLogo),
          awayTeamFull: game.awayTeamFull,
          homeTeamFull: game.homeTeamFull,
          awayScore: game.awayScore, // Include scores for finished/live games
          homeScore: game.homeScore, // Include scores for finished/live games
          gameTime: game.gameTime || game.time,
          status: game.status || 'Scheduled',
          gameDate: game.date,
          startTimeEST: game.startTimeEST, // Include EST time for proper date display
          sport: 'ncaaf',
          week: game.week || week,
          venue: game.venue,
          network: game.network,
          // Conference metadata for the badge / filter UI (mirrors NCAAB).
          // Prefer the shortName variant so badges stay compact (e.g. "SEC"
          // rather than "Southeastern Conference"). Fall back through the
          // longer/alternate keys if the short field is absent.
          homeConferenceName: game.homeConferenceShortName || game.homeConferenceName || game.homeConference || game.homeConf || null,
          awayConferenceName: game.awayConferenceShortName || game.awayConferenceName || game.awayConference || game.awayConf || null,
          homeRank: game.homeRank ?? game.homeCurrentRank ?? null,
          awayRank: game.awayRank ?? game.awayCurrentRank ?? null,
        })) || [];
        
        // Sort games by status - move finished games to bottom
        const sortedGames = sortGamesByStatus(transformedGames);
        
        setGames(sortedGames);
        setGamesMetadata({
          success: true,
          sport: 'ncaaf',
          week: data.week || week,
          total: transformedGames.length,
          date: data.date,
          lastUpdate: new Date().toISOString()
        });
      } else {
        throw new Error(data.message || 'Failed to fetch games');
      }
    } catch (err) {
      console.error('Error fetching NCAAF games:', err);
      setError(err.message);
      // On a silent refresh (visibilitychange), keep whatever games we already
      // have. Mobile browsers throttle background tabs and can kill in-flight
      // fetches; wiping state here would drop the user onto the App-level
      // "Loading games..." overlay on return, forcing a manual refresh.
      if (!silent) {
        setGames([]);
        setGamesMetadata({
          success: false,
          error: err.message,
          sport: 'ncaaf'
        });
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedSport === 'ncaaf') {
      // If we have a selectedWeek, use it, otherwise fetch current week
      const weekParam = selectedWeek ? selectedWeek : 'today';
      console.log('useNcaafGames: Fetching games for week:', weekParam, 'selectedWeek:', selectedWeek);
      fetchGamesForWeek(weekParam);

      // Silent refresh when the tab returns from the background — matches
      // useGamesData/useNflGames so mobile users don't come back to a stale
      // list (and never see a loading flash on the return).
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          fetchGamesForWeek(weekParam, true);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    } else {
      // Clear data when switching away from NCAAF
      setGames([]);
      setGamesMetadata(null);
      setError(null);
    }
  }, [selectedSport, selectedWeek]);

  const refetchGames = () => {
    if (selectedSport === 'ncaaf') {
      const weekParam = selectedWeek ? selectedWeek : 'today';
      fetchGamesForWeek(weekParam);
    }
  };

  return {
    games,
    gamesMetadata,
    isLoading,
    error,
    refetchGames
  };
};